import React, { useState, useCallback, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  Scale, TrendingUp, FileBarChart, Wallet, Briefcase, BarChart3,
  RefreshCw, Database, Download, ChevronDown, ChevronRight, AlertCircle,
  FileText, Printer, Building2
} from 'lucide-react';

const TAB_DEFS = [
  { key: 'trial-balance', label: 'Trial Balance / ميزان المراجعة', icon: Scale },
  { key: 'income-statement', label: 'Income Statement / قائمة الدخل', icon: TrendingUp },
  { key: 'balance-sheet', label: 'Balance Sheet / الميزانية العمومية', icon: FileBarChart },
  { key: 'cash-flow', label: 'Cash Flow / التدفقات النقدية', icon: Wallet },
  { key: 'project-profit', label: 'Project Profit / ربحية المشاريع', icon: Briefcase },
  { key: 'project-cost-analysis', label: 'Project Cost Analysis / تحليل تكلفة المشاريع', icon: BarChart3 },
  { key: 'project-statement', label: 'Project Statement / كشف حساب المشروع', icon: FileText },
  { key: 'company-statement', label: 'Company Statement / كشف حساب الشركة', icon: Building2 },
];

const fmt = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AccountingReports = ({ user }) => {
  const { t } = useTranslation();
  const initParams = new URLSearchParams(window.location.search);
  const initTab = TAB_DEFS.some((d) => d.key === initParams.get('tab')) ? initParams.get('tab') : 'trial-balance';
  const [tab, setTab] = useState(initTab);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dataTab, setDataTab] = useState(null);  // tab key that `data` belongs to
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(initParams.get('project_id') || '');

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  useEffect(() => {
    axiosInstance.get('/projects')
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    if (tab === 'project-statement' && !projectId) {
      setData(null); setDataTab(null);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab === 'trial-balance' || tab === 'balance-sheet') {
        if (dateTo) params.append('as_of', dateTo);
      } else {
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
      }
      if (tab === 'project-statement') params.append('project_id', projectId);
      const url = `/accounting/${tab}${params.toString() ? `?${params}` : ''}`;
      const res = await axiosInstance.get(url);
      setData(res.data);
      setDataTab(tab);
    } catch (e) {
      toast.error(`فشل تحميل التقرير: ${e?.response?.data?.detail || e.message}`);
      setData(null);
      setDataTab(null);
    } finally {
      setLoading(false);
    }
  }, [tab, dateFrom, dateTo, projectId]);

  // Clear data immediately when the tab changes so we don't render the wrong
  // report component with the previous tab's shape on the first paint.
  useEffect(() => { setData(null); setDataTab(null); }, [tab]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleSeed = async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const res = await axiosInstance.post('/accounting/seed-coa');
      toast.success(`تم تهيئة شجرة الحسابات: أُنشئ ${res.data.created} • محدث ${res.data.updated} • متجاهل ${res.data.skipped}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل');
    } finally {
      setBusy(false);
    }
  };

  const handleBackfill = async (purge = false) => {
    if (!isAdmin) return;
    if (purge && !window.confirm('سيتم حذف جميع القيود التلقائية القديمة وإعادة إنشائها. هل أنت متأكد؟')) return;
    setBusy(true);
    try {
      const res = await axiosInstance.post(`/accounting/backfill?purge_legacy=${purge}`);
      const s = res.data;
      toast.success(`Backfill تم: فواتير ${s.invoices} • سندات قبض/صرف ${s.payments} • فواتير موردين ${s.supplier_bills} • مصاريف ${s.expenses} • رواتب ${s.payroll}${s.purged ? ` • حُذف ${s.purged}` : ''}`);
      if (s.errors?.length) toast.warning(`${s.errors.length} أخطاء — راجع الإجابة في console.`);
      fetchReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل Backfill');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="accounting-reports-page">
      {/* Header & actions */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Accounting Reports</h1>
          <p className="text-sm text-gray-500 mt-1">التقارير المحاسبية المعتمدة على القيود المزدوجة</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button data-testid="seed-coa-btn" onClick={handleSeed} disabled={busy}
              className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm flex items-center gap-2 disabled:opacity-50">
              <Database className="w-4 h-4" /> Seed Chart of Accounts
            </button>
            <button data-testid="backfill-btn" onClick={() => handleBackfill(false)} disabled={busy}
              className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm flex items-center gap-2 disabled:opacity-50">
              <RefreshCw className="w-4 h-4" /> Backfill Journals
            </button>
            <button data-testid="backfill-purge-btn" onClick={() => handleBackfill(true)} disabled={busy}
              className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm flex items-center gap-2 disabled:opacity-50">
              <AlertCircle className="w-4 h-4" /> Purge & Backfill
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {TAB_DEFS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition ${
                active ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
        {tab === 'project-statement' && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">المشروع</label>
            <select data-testid="project-statement-select" value={projectId} onChange={(e) => setProjectId(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[220px] bg-white">
              <option value="">— اختر مشروعاً —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.project_code ? `${p.project_code} — ` : ''}{p.name}</option>
              ))}
            </select>
          </div>
        )}
        {(tab !== 'trial-balance' && tab !== 'balance-sheet') && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">من تاريخ</label>
            <input type="date" data-testid="date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-600 mb-1">
            {(tab === 'trial-balance' || tab === 'balance-sheet') ? 'كما في تاريخ' : 'إلى تاريخ'}
          </label>
          <input type="date" data-testid="date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
        </div>
        <button data-testid="refresh-btn" onClick={fetchReport} disabled={loading}
          className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Report content */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {loading && <div className="p-10 text-center text-gray-500">جاري التحميل...</div>}
        {!loading && !data && tab === 'project-statement' && !projectId && (
          <div className="p-10 text-center text-gray-400" data-testid="project-statement-empty">اختر مشروعاً من القائمة أعلاه لعرض كشف الحساب.</div>
        )}
        {!loading && !data && !(tab === 'project-statement' && !projectId) && <div className="p-10 text-center text-gray-400">لا توجد بيانات.</div>}
        {!loading && data && dataTab === tab && tab === 'trial-balance' && <TrialBalance data={data} />}
        {!loading && data && dataTab === tab && tab === 'income-statement' && <IncomeStatement data={data} />}
        {!loading && data && dataTab === tab && tab === 'balance-sheet' && <BalanceSheet data={data} />}
        {!loading && data && dataTab === tab && tab === 'cash-flow' && <CashFlow data={data} />}
        {!loading && data && dataTab === tab && tab === 'project-profit' && <ProjectProfit data={data} />}
        {!loading && data && dataTab === tab && tab === 'project-cost-analysis' && <ProjectCostAnalysis data={data} />}
        {!loading && data && dataTab === tab && tab === 'project-statement' && <ProjectStatement data={data} />}
        {!loading && data && dataTab === tab && tab === 'company-statement' && <CompanyStatement data={data} />}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Report components
// ─────────────────────────────────────────────────────────────────
const Th = ({ children, align = 'left' }) => (
  <th className={`px-3 py-2 text-${align} text-[11px] uppercase text-gray-500 font-semibold border-b border-gray-200`}>{children}</th>
);
const Td = ({ children, align = 'left', className = '' }) => (
  <td className={`px-3 py-2 text-sm text-gray-700 text-${align} ${className}`}>{children}</td>
);

const TrialBalance = ({ data }) => (
  <div data-testid="trial-balance-report" className="overflow-x-auto">
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div className="text-sm text-gray-600">
        كما في: <strong>{data.as_of || 'اليوم'}</strong>
      </div>
      <div className={`text-sm font-semibold ${data.balanced ? 'text-emerald-600' : 'text-red-600'}`}>
        {data.balanced ? '✓ متوازن' : '✗ غير متوازن'}
      </div>
    </div>
    <table className="w-full">
      <thead className="bg-gray-50">
        <tr>
          <Th>رقم</Th>
          <Th>الحساب</Th>
          <Th>النوع</Th>
          <Th align="right">إجمالي المدين</Th>
          <Th align="right">إجمالي الدائن</Th>
          <Th align="right">رصيد مدين</Th>
          <Th align="right">رصيد دائن</Th>
        </tr>
      </thead>
      <tbody>
        {data.accounts.map((a, idx) => (
          <tr key={a.account_id || idx} className="border-b border-gray-100 hover:bg-gray-50">
            <Td className="font-mono text-xs">{a.account_number || '—'}</Td>
            <Td>{a.account_name}</Td>
            <Td className="text-xs uppercase text-gray-500">{a.account_type || '—'}</Td>
            <Td align="right" className="font-mono">{fmt(a.total_debit)}</Td>
            <Td align="right" className="font-mono">{fmt(a.total_credit)}</Td>
            <Td align="right" className="font-mono text-emerald-700">{a.debit_balance ? fmt(a.debit_balance) : ''}</Td>
            <Td align="right" className="font-mono text-red-700">{a.credit_balance ? fmt(a.credit_balance) : ''}</Td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-gray-50 font-semibold">
        <tr>
          <Td colSpan={5} className="text-right">Total</Td>
          <Td align="right" className="font-mono text-emerald-700">{fmt(data.total_debit)}</Td>
          <Td align="right" className="font-mono text-red-700">{fmt(data.total_credit)}</Td>
        </tr>
      </tfoot>
    </table>
  </div>
);

const Section = ({ title, lines, total, color = 'gray' }) => (
  <div className="mb-6">
    <h3 className={`text-sm font-bold mb-2 px-4 py-1.5 bg-${color}-50 text-${color}-800 border-l-4 border-${color}-500`}>{title}</h3>
    <table className="w-full">
      <tbody>
        {lines.map((l, i) => (
          <tr key={i} className="border-b border-gray-100">
            <Td className="font-mono text-xs w-20">{l.account_number || '—'}</Td>
            <Td>{l.account_name}</Td>
            <Td align="right" className="font-mono">{fmt(l.amount)}</Td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-gray-50">
        <tr>
          <Td colSpan={2} className="font-semibold">Total {title}</Td>
          <Td align="right" className="font-mono font-semibold">{fmt(total)}</Td>
        </tr>
      </tfoot>
    </table>
  </div>
);

const IncomeStatement = ({ data }) => {
  const { t } = useTranslation();
  const rev = data?.revenue || { lines: [], total: 0 };
  const cost = data?.cost_of_projects || { lines: [], total: 0 };
  const admin = data?.admin_expenses || { lines: [], total: 0 };
  return (
    <div data-testid="income-statement-report" className="p-4">
      <Section title={t('accountingreports.revenue')} lines={rev.lines} total={rev.total} color="emerald" />
      <Section title={t('accountingreports.cost_of_projects')} lines={cost.lines} total={cost.total} color="orange" />
      <div className="mb-4 px-4 py-2 bg-blue-50 border-l-4 border-blue-500 flex justify-between text-blue-800 font-semibold">
        <span>Gross Profit / مجمل الربح</span>
        <span className="font-mono">{fmt(data?.gross_profit)}</span>
      </div>
      <Section title={t('accountingreports.administrative_expenses')} lines={admin.lines} total={admin.total} color="red" />
      <div className={`px-4 py-3 ${(data?.net_profit || 0) >= 0 ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-900'} border-l-4 ${(data?.net_profit || 0) >= 0 ? 'border-emerald-600' : 'border-red-600'} flex justify-between font-bold text-lg`}>
        <span>Net Profit / صافي الربح</span>
        <span className="font-mono">{fmt(data?.net_profit)}</span>
      </div>
    </div>
  );
};

const BalanceSheet = ({ data }) => {
  const { t } = useTranslation();
  const assets = data?.assets || { lines: [], total: 0 };
  const liabs = data?.liabilities || { lines: [], total: 0 };
  const equity = data?.equity || { lines: [], total: 0 };
  return (
    <div data-testid="balance-sheet-report" className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">كما في: <strong>{data?.as_of || 'اليوم'}</strong></div>
        <div className={`text-sm font-semibold ${data?.balanced ? 'text-emerald-600' : 'text-red-600'}`}>
          {data?.balanced ? '✓ الميزانية متوازنة' : '✗ غير متوازنة'}
        </div>
      </div>
      <Section title={t('accountingreports.assets')} lines={assets.lines} total={assets.total} color="emerald" />
      <Section title={t('accountingreports.liabilities')} lines={liabs.lines} total={liabs.total} color="orange" />
      <Section title={t('accountingreports.equity')} lines={equity.lines} total={equity.total} color="indigo" />
      <div className="px-4 py-2 bg-blue-50 border-l-4 border-blue-500 flex justify-between text-blue-800 font-semibold">
        <span>Total Liab + Equity</span>
        <span className="font-mono">{fmt(data?.total_liab_equity)}</span>
      </div>
    </div>
  );
};

const CashFlow = ({ data }) => {
  const details = Array.isArray(data?.details) ? data.details : [];
  return (
    <div data-testid="cash-flow-report" className="overflow-x-auto">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 grid grid-cols-3 gap-4 text-sm">
        <div><span className="text-gray-500">تدفقات داخلة:</span> <strong className="text-emerald-700 font-mono ml-2">{fmt(data?.inflows)}</strong></div>
        <div><span className="text-gray-500">تدفقات خارجة:</span> <strong className="text-red-700 font-mono ml-2">{fmt(data?.outflows)}</strong></div>
        <div><span className="text-gray-500">صافي التدفق:</span> <strong className={`font-mono ml-2 ${(data?.net || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(data?.net)}</strong></div>
      </div>
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th>التاريخ</Th>
            <Th>القيد</Th>
            <Th>الحساب</Th>
            <Th>الوصف</Th>
            <Th align="right">داخل</Th>
            <Th align="right">خارج</Th>
          </tr>
        </thead>
        <tbody>
          {details.map((d, i) => (
            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
              <Td>{d.date}</Td>
              <Td className="font-mono text-xs">{d.journal_no}</Td>
              <Td>{d.account}</Td>
              <Td className="text-xs text-gray-600">{d.description}</Td>
              <Td align="right" className="font-mono text-emerald-700">{d.inflow ? fmt(d.inflow) : ''}</Td>
              <Td align="right" className="font-mono text-red-700">{d.outflow ? fmt(d.outflow) : ''}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProjectProfit = ({ data }) => {
  const projects = Array.isArray(data?.projects) ? data.projects : [];
  const totals = data?.totals || { revenue: 0, cost: 0, profit: 0 };
  return (
    <div data-testid="project-profit-report" className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th>كود</Th>
            <Th>المشروع</Th>
            <Th align="right">قيمة العقد</Th>
            <Th align="right">الإيراد</Th>
            <Th align="right">التكلفة</Th>
            <Th align="right">الربح</Th>
            <Th align="right">هامش %</Th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.project_id} className="border-b border-gray-100 hover:bg-gray-50">
              <Td className="font-mono text-xs">{p.project_code || '—'}</Td>
              <Td>{p.project_name || '—'}</Td>
              <Td align="right" className="font-mono">{fmt(p.contract_value)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmt(p.revenue)}</Td>
              <Td align="right" className="font-mono text-orange-700">{fmt(p.cost)}</Td>
              <Td align="right" className={`font-mono font-semibold ${(p.profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(p.profit)}</Td>
              <Td align="right" className="font-mono">{fmt(p.margin_pct)}%</Td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50 font-semibold">
          <tr>
            <Td colSpan={3} className="text-right">Total</Td>
            <Td align="right" className="font-mono">{fmt(totals.revenue)}</Td>
            <Td align="right" className="font-mono">{fmt(totals.cost)}</Td>
            <Td align="right" className="font-mono">{fmt(totals.profit)}</Td>
            <Td></Td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const BUCKET_COLORS = {
  materials: 'bg-blue-500',
  labor: 'bg-emerald-500',
  subcontractors: 'bg-amber-500',
  equipment: 'bg-slate-500',
  other: 'bg-purple-500',
};
const BUCKET_LABELS = {
  materials: 'مواد',
  labor: 'عمالة',
  subcontractors: 'مقاولي باطن',
  equipment: 'معدات',
  other: 'أخرى',
};

const CostBar = ({ breakdown, total }) => {
  if (!total) return <div className="w-full h-2 bg-gray-100 rounded" />;
  return (
    <div className="w-full h-2 bg-gray-100 rounded overflow-hidden flex">
      {Object.entries(breakdown).map(([k, v]) => {
        const pct = total ? (v / total) * 100 : 0;
        if (pct <= 0) return null;
        return <div key={k} className={BUCKET_COLORS[k]} style={{ width: `${pct}%` }} title={`${BUCKET_LABELS[k]}: ${fmt(v)}`} />;
      })}
    </div>
  );
};

const ProjectCostAnalysis = ({ data }) => {
  const [expanded, setExpanded] = useState(null);
  // Defensive: backend may not be deployed; fall back to empty projects list
  const projectsList = Array.isArray(data?.projects) ? data.projects : [];
  const t = data?.totals?.cost_breakdown || {};
  const allTotal = data?.totals?.cost || 0;
  return (
    <div data-testid="project-cost-analysis-report" className="space-y-4">
      {/* KPIs */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><div className="text-xs text-gray-500">Total Revenue</div><div className="text-lg font-mono font-bold text-emerald-700">{fmt(data?.totals?.revenue)}</div></div>
        <div><div className="text-xs text-gray-500">Total Cost</div><div className="text-lg font-mono font-bold text-orange-700">{fmt(data?.totals?.cost)}</div></div>
        <div><div className="text-xs text-gray-500">Total Profit</div><div className={`text-lg font-mono font-bold ${(data?.totals?.profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(data?.totals?.profit)}</div></div>
        <div><div className="text-xs text-gray-500">Contract Value</div><div className="text-lg font-mono font-bold text-blue-700">{fmt(data?.totals?.contract_value)}</div></div>
      </div>
      <div className="px-4 flex flex-wrap gap-3 text-xs">
        {Object.keys(BUCKET_LABELS).map((k) => (
          <div key={k} className="flex items-center gap-2">
            <span className={`inline-block w-3 h-3 rounded ${BUCKET_COLORS[k]}`} />
            <span className="text-gray-700">{BUCKET_LABELS[k]}: <strong className="font-mono">{fmt(t[k])}</strong></span>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <Th></Th>
              <Th>Project</Th>
              <Th align="right">Contract</Th>
              <Th align="right">Revenue</Th>
              <Th align="right">Materials</Th>
              <Th align="right">Labor</Th>
              <Th align="right">Subs</Th>
              <Th align="right">Equip</Th>
              <Th align="right">Other</Th>
              <Th align="right">Total Cost</Th>
              <Th align="right">Profit</Th>
              <Th align="right">Margin %</Th>
            </tr>
          </thead>
          <tbody>
            {projectsList.map((p) => {
              const isOpen = expanded === p.project_id;
              return (
                <React.Fragment key={p.project_id}>
                  <tr data-testid={`pca-row-${p.project_id}`} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(isOpen ? null : p.project_id)}>
                    <Td>{isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}</Td>
                    <Td><div className="font-medium">{p.project_name}</div><div className="text-[10px] text-gray-500 font-mono">{p.project_code || ''}</div></Td>
                    <Td align="right" className="font-mono text-blue-700">{fmt(p.contract_value)}</Td>
                    <Td align="right" className="font-mono text-emerald-700">{fmt(p.revenue)}</Td>
                    <Td align="right" className="font-mono">{fmt(p.cost_breakdown?.materials)}</Td>
                    <Td align="right" className="font-mono">{fmt(p.cost_breakdown?.labor)}</Td>
                    <Td align="right" className="font-mono">{fmt(p.cost_breakdown?.subcontractors)}</Td>
                    <Td align="right" className="font-mono">{fmt(p.cost_breakdown?.equipment)}</Td>
                    <Td align="right" className="font-mono">{fmt(p.cost_breakdown?.other)}</Td>
                    <Td align="right" className="font-mono text-orange-700">{fmt(p.total_cost)}</Td>
                    <Td align="right" className={`font-mono font-semibold ${p.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(p.profit)}</Td>
                    <Td align="right" className="font-mono">{fmt(p.margin_pct)}%</Td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50">
                      <td colSpan={12} className="p-4">
                        <div className="text-xs font-semibold text-gray-600 mb-2">Cost mix breakdown</div>
                        <CostBar breakdown={p.cost_breakdown || {}} total={p.total_cost} />
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                          {Object.keys(BUCKET_LABELS).map((k) => {
                            const v = (p.cost_breakdown || {})[k] || 0;
                            const pct = p.total_cost ? ((v / p.total_cost) * 100).toFixed(1) : '0.0';
                            return (
                              <div key={k} className="bg-white border border-gray-200 rounded px-2 py-1">
                                <div className="flex items-center gap-1">
                                  <span className={`inline-block w-2 h-2 rounded ${BUCKET_COLORS[k]}`} />
                                  <span className="text-gray-600">{BUCKET_LABELS[k]}</span>
                                </div>
                                <div className="font-mono font-semibold">{fmt(v)} <span className="text-gray-400 font-normal">({pct}%)</span></div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-3 text-xs text-gray-600">
                          <strong>Billed vs Contract:</strong>{' '}
                          <span className={p.variance_billed_vs_contract > 0 ? 'text-amber-700' : 'text-emerald-700'}>
                            {fmt(p.variance_billed_vs_contract)} {p.variance_billed_vs_contract > 0 ? '(unbilled remaining)' : '(over-billed)'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-100 font-semibold">
            <tr>
              <Td></Td>
              <Td>Total</Td>
              <Td align="right" className="font-mono text-blue-700">{fmt(data?.totals?.contract_value)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmt(data?.totals?.revenue)}</Td>
              <Td align="right" className="font-mono">{fmt(t.materials)}</Td>
              <Td align="right" className="font-mono">{fmt(t.labor)}</Td>
              <Td align="right" className="font-mono">{fmt(t.subcontractors)}</Td>
              <Td align="right" className="font-mono">{fmt(t.equipment)}</Td>
              <Td align="right" className="font-mono">{fmt(t.other)}</Td>
              <Td align="right" className="font-mono text-orange-700">{fmt(allTotal)}</Td>
              <Td align="right" className={`font-mono ${(data?.totals?.profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(data?.totals?.profit)}</Td>
              <Td></Td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const REF_TYPE_LABELS = {
  invoice: 'فاتورة عميل',
  payment: 'سند قبض/صرف',
  supplier_bill: 'فاتورة مورد',
  expense: 'مصروف',
  expense_report: 'مصروف',
  payroll: 'رواتب',
  bank_adjustment: 'تسوية بنكية',
  bank_transaction: 'حركة بنك',
  gov_utility: 'دفعة حكومية/خدمات',
  manual: 'قيد يدوي',
  credit_note: 'إشعار دائن',
};

const ProjectStatement = ({ data }) => {
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  const s = data?.summary || {};
  const proj = data?.project || {};
  const period = `${data?.date_from || 'البداية'} → ${data?.date_to || 'اليوم'}`;

  const exportCSV = () => {
    const head = ['Date', 'Journal No', 'Source', 'Description', 'Account', 'Debit', 'Credit', 'Balance'];
    const rows = entries.map((e) => [
      e.date, e.journal_no, REF_TYPE_LABELS[e.reference_type] || e.reference_type || '',
      (e.description || '').replace(/"/g, '""'),
      `${e.account_number} ${e.account_name}`, e.debit, e.credit, e.balance,
    ]);
    const csv = '\uFEFF' + [head, ...rows].map((r) => r.map((c) => `"${c ?? ''}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `project-statement-${proj.project_code || proj.name || 'export'}.csv`;
    a.click();
  };

  const printStatement = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const rowsHtml = entries.map((e) => `
      <tr>
        <td>${e.date || ''}</td><td>${e.journal_no || ''}</td>
        <td>${REF_TYPE_LABELS[e.reference_type] || e.reference_type || ''}</td>
        <td>${e.description || ''}</td>
        <td>${e.account_number || ''} ${e.account_name || ''}</td>
        <td class="num">${e.debit ? fmt(e.debit) : ''}</td>
        <td class="num">${e.credit ? fmt(e.credit) : ''}</td>
        <td class="num"><strong>${fmt(e.balance)}</strong></td>
      </tr>`).join('');
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"><title>كشف حساب مشروع</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        h1{font-size:20px;margin:0} .sub{color:#555;font-size:13px;margin:4px 0 16px}
        .cards{display:flex;gap:12px;margin-bottom:16px;flex-wrap:wrap}
        .card{border:1px solid #ddd;border-radius:8px;padding:8px 14px;font-size:13px}
        .card b{display:block;font-size:16px;font-family:monospace}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ddd;padding:5px 7px;text-align:right}
        th{background:#f3f4f6} .num{font-family:monospace;direction:ltr;text-align:left}
      </style></head><body>
      <h1>كشف حساب المشروع: ${proj.name || ''} ${proj.project_code ? `(${proj.project_code})` : ''}</h1>
      <div class="sub">الفترة: ${period} • عدد الحركات: ${data?.entry_count || 0}</div>
      <div class="cards">
        <div class="card">الإيرادات<b>${fmt(s.revenue)}</b></div>
        <div class="card">تكاليف مباشرة (51)<b>${fmt(s.direct_cost)}</b></div>
        <div class="card">مصاريف غير مباشرة<b>${fmt((s.overhead || 0) + (s.other_expenses || 0))}</b></div>
        <div class="card">إجمالي التكاليف<b>${fmt(s.total_cost)}</b></div>
        <div class="card">صافي الربح<b>${fmt(s.net_profit)}</b></div>
      </div>
      <table><thead><tr><th>التاريخ</th><th>رقم القيد</th><th>المصدر</th><th>البيان</th><th>الحساب</th><th>مدين</th><th>دائن</th><th>الرصيد الجاري</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="project-statement-report">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div>
          <div className="font-semibold text-gray-900">{proj.name} {proj.project_code ? <span className="text-xs font-mono text-gray-500">({proj.project_code})</span> : null}</div>
          <div className="text-xs text-gray-500 mt-0.5">الفترة: {period} • عدد الحركات: {data?.entry_count || 0} • قيمة العقد: <span className="font-mono">{fmt(proj.contract_value)}</span></div>
        </div>
        <div className="flex gap-2">
          <button data-testid="project-statement-print-btn" onClick={printStatement}
            className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm flex items-center gap-2">
            <Printer className="w-4 h-4" /> طباعة
          </button>
          <button data-testid="project-statement-export-btn" onClick={exportCSV}
            className="px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> تصدير CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-5 gap-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-emerald-50">
        <div><div className="text-xs text-gray-500">الإيرادات</div><div className="text-lg font-mono font-bold text-emerald-700" data-testid="ps-revenue">{fmt(s.revenue)}</div></div>
        <div><div className="text-xs text-gray-500">تكاليف مباشرة (51)</div><div className="text-lg font-mono font-bold text-orange-700">{fmt(s.direct_cost)}</div></div>
        <div><div className="text-xs text-gray-500">مصاريف غير مباشرة</div><div className="text-lg font-mono font-bold text-amber-700">{fmt((s.overhead || 0) + (s.other_expenses || 0))}</div></div>
        <div><div className="text-xs text-gray-500">إجمالي التكاليف</div><div className="text-lg font-mono font-bold text-red-700">{fmt(s.total_cost)}</div></div>
        <div><div className="text-xs text-gray-500">صافي الربح</div><div className={`text-lg font-mono font-bold ${(s.net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="ps-net-profit">{fmt(s.net_profit)}</div></div>
      </div>

      {/* Entries table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <Th>التاريخ</Th>
              <Th>رقم القيد</Th>
              <Th>المصدر</Th>
              <Th>البيان</Th>
              <Th>الحساب</Th>
              <Th align="right">مدين</Th>
              <Th align="right">دائن</Th>
              <Th align="right">الرصيد الجاري</Th>
            </tr>
          </thead>
          <tbody>
            {data?.date_from && (
              <tr className="bg-blue-50/50 border-b border-gray-100">
                <Td colSpan={7} className="text-xs text-gray-600 font-medium">رصيد افتتاحي (قبل {data.date_from})</Td>
                <Td align="right" className="font-mono font-semibold">{fmt(data.opening_balance)}</Td>
              </tr>
            )}
            {entries.length === 0 && (
              <tr><Td colSpan={8} className="text-center text-gray-400 py-8">لا توجد حركات لهذا المشروع في الفترة المحددة.</Td></tr>
            )}
            {entries.map((e, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`ps-row-${i}`}>
                <Td className="font-mono text-xs whitespace-nowrap">{e.date}</Td>
                <Td className="font-mono text-xs">{e.journal_no}</Td>
                <Td className="text-xs">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${e.account_type === 'revenue' || e.account_type === 'income' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                    {REF_TYPE_LABELS[e.reference_type] || e.reference_type || '—'}
                  </span>
                </Td>
                <Td className="text-xs text-gray-600 max-w-[280px] truncate" title={e.description}>{e.description}</Td>
                <Td className="text-xs"><span className="font-mono text-gray-400">{e.account_number}</span> {e.account_name}</Td>
                <Td align="right" className="font-mono text-red-700">{e.debit ? fmt(e.debit) : ''}</Td>
                <Td align="right" className="font-mono text-emerald-700">{e.credit ? fmt(e.credit) : ''}</Td>
                <Td align="right" className={`font-mono font-semibold ${(e.balance || 0) >= 0 ? 'text-gray-800' : 'text-red-700'}`}>{fmt(e.balance)}</Td>
              </tr>
            ))}
          </tbody>
          {entries.length > 0 && (
            <tfoot className="bg-gray-100 font-semibold">
              <tr>
                <Td colSpan={5} className="text-right">الإجمالي / الرصيد الختامي</Td>
                <Td align="right" className="font-mono text-red-700">{fmt(entries.reduce((a, e) => a + (e.debit || 0), 0))}</Td>
                <Td align="right" className="font-mono text-emerald-700">{fmt(entries.reduce((a, e) => a + (e.credit || 0), 0))}</Td>
                <Td align="right" className={`font-mono ${(data?.closing_balance || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="ps-closing-balance">{fmt(data?.closing_balance)}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

const CAT_LABELS = {
  project: { txt: 'تكاليف مشاريع (51)', cls: 'bg-blue-100 text-blue-700' },
  admin: { txt: 'مصاريف إدارية (52)', cls: 'bg-amber-100 text-amber-700' },
  operating: { txt: 'مصاريف تشغيلية (53)', cls: 'bg-purple-100 text-purple-700' },
  other: { txt: 'مصاريف أخرى', cls: 'bg-slate-100 text-slate-600' },
};

const CompanyStatement = ({ data }) => {
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  const s = data?.summary || {};
  const period = `${data?.date_from || 'البداية'} → ${data?.date_to || 'اليوم'}`;

  const exportCSV = () => {
    const head = ['Date', 'Journal No', 'Source', 'Description', 'Account', 'Category', 'Project', 'Debit', 'Credit', 'Running Total'];
    const rows = entries.map((e) => [
      e.date, e.journal_no, REF_TYPE_LABELS[e.reference_type] || e.reference_type || '',
      (e.description || '').replace(/"/g, '""'),
      `${e.account_number} ${e.account_name}`, CAT_LABELS[e.category]?.txt || '',
      e.project_name || 'الشركة', e.debit, e.credit, e.running_total,
    ]);
    const csv = '\uFEFF' + [head, ...rows].map((r) => r.map((c) => `"${c ?? ''}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'company-statement.csv';
    a.click();
  };

  const printStatement = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const rowsHtml = entries.map((e) => `
      <tr><td>${e.date || ''}</td><td>${e.journal_no || ''}</td>
      <td>${CAT_LABELS[e.category]?.txt || ''}</td>
      <td>${e.description || ''}</td>
      <td>${e.account_number || ''} ${e.account_name || ''}</td>
      <td>${e.project_name || 'الشركة'}</td>
      <td class="num">${e.debit ? fmt(e.debit) : ''}</td>
      <td class="num">${e.credit ? fmt(e.credit) : ''}</td>
      <td class="num"><strong>${fmt(e.running_total)}</strong></td></tr>`).join('');
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"><title>كشف حساب الشركة</title>
      <style>body{font-family:Arial;padding:24px;color:#111}h1{font-size:20px;margin:0}
      .sub{color:#555;font-size:13px;margin:4px 0 16px}
      .cards{display:flex;gap:12px;margin-bottom:16px;flex-wrap:wrap}
      .card{border:1px solid #ddd;border-radius:8px;padding:8px 14px;font-size:13px}
      .card b{display:block;font-size:16px;font-family:monospace}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th,td{border:1px solid #ddd;padding:4px 6px;text-align:right}
      th{background:#f3f4f6}.num{font-family:monospace;direction:ltr;text-align:left}</style></head><body>
      <h1>كشف حساب الشركة — جميع المصاريف</h1>
      <div class="sub">الفترة: ${period} • عدد الحركات: ${data?.entry_count || 0}</div>
      <div class="cards">
        <div class="card">تكاليف مشاريع (51)<b>${fmt(s.project_costs)}</b></div>
        <div class="card">مصاريف إدارية (52)<b>${fmt(s.admin_expenses)}</b></div>
        <div class="card">مصاريف تشغيلية (53)<b>${fmt(s.operating_expenses)}</b></div>
        <div class="card">أخرى<b>${fmt(s.other_expenses)}</b></div>
        <div class="card">الإجمالي<b>${fmt(s.total)}</b></div>
      </div>
      <table><thead><tr><th>التاريخ</th><th>القيد</th><th>التصنيف</th><th>البيان</th><th>الحساب</th><th>المشروع</th><th>مدين</th><th>دائن</th><th>الإجمالي التراكمي</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="company-statement-report">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div>
          <div className="font-semibold text-gray-900">كشف حساب الشركة — جميع المصاريف</div>
          <div className="text-xs text-gray-500 mt-0.5">الفترة: {period} • عدد الحركات: {data?.entry_count || 0}</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="company-statement-print-btn" onClick={printStatement}
            className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm flex items-center gap-2">
            <Printer className="w-4 h-4" /> طباعة
          </button>
          <button data-testid="company-statement-export-btn" onClick={exportCSV}
            className="px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> تصدير CSV
          </button>
        </div>
      </div>

      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-5 gap-3 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-blue-50">
        <div><div className="text-xs text-gray-500">تكاليف مشاريع (51)</div><div className="text-lg font-mono font-bold text-blue-700" data-testid="cs-project-costs">{fmt(s.project_costs)}</div></div>
        <div><div className="text-xs text-gray-500">مصاريف إدارية (52)</div><div className="text-lg font-mono font-bold text-amber-700" data-testid="cs-admin">{fmt(s.admin_expenses)}</div></div>
        <div><div className="text-xs text-gray-500">مصاريف تشغيلية (53)</div><div className="text-lg font-mono font-bold text-purple-700">{fmt(s.operating_expenses)}</div></div>
        <div><div className="text-xs text-gray-500">مصاريف أخرى</div><div className="text-lg font-mono font-bold text-slate-600">{fmt(s.other_expenses)}</div></div>
        <div><div className="text-xs text-gray-500">إجمالي المصاريف</div><div className="text-lg font-mono font-bold text-red-700" data-testid="cs-total">{fmt(s.total)}</div></div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <Th>التاريخ</Th><Th>القيد</Th><Th>التصنيف</Th><Th>البيان</Th>
              <Th>الحساب</Th><Th>المشروع</Th>
              <Th align="right">مدين</Th><Th align="right">دائن</Th>
              <Th align="right">الإجمالي التراكمي</Th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><Td colSpan={9} className="text-center text-gray-400 py-8">لا توجد مصاريف في الفترة المحددة.</Td></tr>
            )}
            {entries.map((e, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`cs-row-${i}`}>
                <Td className="font-mono text-xs whitespace-nowrap">{e.date}</Td>
                <Td className="font-mono text-xs">{e.journal_no}</Td>
                <Td className="text-xs">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${CAT_LABELS[e.category]?.cls || ''}`}>
                    {CAT_LABELS[e.category]?.txt || e.category}
                  </span>
                </Td>
                <Td className="text-xs text-gray-600 max-w-[240px] truncate" title={e.description}>{e.description}</Td>
                <Td className="text-xs"><span className="font-mono text-gray-400">{e.account_number}</span> {e.account_name}</Td>
                <Td className="text-xs">{e.project_name
                  ? <span className="text-blue-700">{e.project_name}</span>
                  : <span className="text-amber-700 font-medium">الشركة</span>}</Td>
                <Td align="right" className="font-mono text-red-700">{e.debit ? fmt(e.debit) : ''}</Td>
                <Td align="right" className="font-mono text-emerald-700">{e.credit ? fmt(e.credit) : ''}</Td>
                <Td align="right" className="font-mono font-semibold">{fmt(e.running_total)}</Td>
              </tr>
            ))}
          </tbody>
          {entries.length > 0 && (
            <tfoot className="bg-gray-100 font-semibold">
              <tr>
                <Td colSpan={6} className="text-right">الإجمالي</Td>
                <Td align="right" className="font-mono text-red-700">{fmt(entries.reduce((a, e) => a + (e.debit || 0), 0))}</Td>
                <Td align="right" className="font-mono text-emerald-700">{fmt(entries.reduce((a, e) => a + (e.credit || 0), 0))}</Td>
                <Td align="right" className="font-mono text-red-700" data-testid="cs-grand-total">{fmt(s.total)}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default AccountingReports;
